import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Cache, Auth } from 'aws-amplify';
import { UserSettingsHttpService } from '../../../../../app/core/user-settings';
import { MatSnackBar } from '@angular/material';
import { MixpanelService } from '../../../../services/mixpanel.service';
import { DemoModeService } from '../../../../../app/core/demo-mode';
import { DevImpersonationService } from '../../../../../app/core/_services/dev-impersonation.service';
import { isOriginalSuperAdminRole } from '../../../../../app/core/_services/impersonation-runtime';
import { VoucherDemoModeService } from '../../../../../app/core/vouchers/demo/voucher-demo-mode.service';
import { VOUCHER_DEMO_ACCOUNT_NAME } from '../../../../../app/core/vouchers/demo/voucher-demo.constants';
import {
	ViewAsScopeService,
	ViewAsScope,
	ViewAsRole
} from '../../../../../app/core/view-as-scope';
import { map } from 'rxjs/operators';
import { formatRoleLabel, PORTAL_VIEW_CLAIM } from '../../../../core/_config/portal-view';
import { SelectOption } from '../../../partials/content/general/searchable-select/searchable-select.component';

interface Distributor {
	disti_id: number;
	name: string;
}

interface Client {
	client_id: number;
	company_name: string;
}

interface Campus {
	campus_id: number;
	name: string;
}

@Component({
  selector: 'kt-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit {

	profileForm: FormGroup;

	userObj;

	region = 'NA';
	territory = 'NA';

	// View As scope (super admins only)
	viewAsEnabled = false;
	viewAsRole: ViewAsRole | null = null;
	viewAsDistiId: string | null = null;
	viewAsClientId: string | null = null;
	viewAsCampusId: string | null = null;

	distributors: SelectOption[] = [];
	clients: SelectOption[] = [];
	campuses: SelectOption[] = [];

	loadingDistributors = false;
	loadingClients = false;
	loadingCampuses = false;
	savingViewAs = false;

	viewAsRoles: { value: ViewAsRole; label: string }[] = [
		{ value: 'distiadmin', label: 'Partner Admin' },
		{ value: 'clientadmin', label: 'Client Admin' },
		{ value: 'campusadmin', label: 'Campus Admin' }
	];

  constructor(
		private fb: FormBuilder,
		private translate: TranslateService,
		private cdr: ChangeDetectorRef,
		private http: UserSettingsHttpService,
		private _snackBar: MatSnackBar,
		private mixePanelService: MixpanelService,
		public demoModeService: DemoModeService,
		private devImpersonationService: DevImpersonationService,
		public viewAsScopeService: ViewAsScopeService,
		public voucherDemoMode: VoucherDemoModeService
	) { }

  ngOnInit() {
	  this.userObj = Cache.getItem('userAttr');
	//   this.parseRegionAndTerritory();
	  this.initForm();
	  this.loadViewAsState();
	  Auth.currentUserInfo()
	  .then(info => {
		console.log(info);
	  });

	  this.mixePanelService.track(this.mixePanelService.PAGE_VIEW);
  }

	get isSuperAdmin(): boolean {
		return this.devImpersonationService.getOriginalRole() === 'sadmin';
	}

	get canToggleRetailAdminDemo(): boolean {
		return this.devImpersonationService.isImpersonationEnabled()
			&& isOriginalSuperAdminRole(this.devImpersonationService.getOriginalRole());
	}

	get retailAdminDemoAccountName(): string {
		return VOUCHER_DEMO_ACCOUNT_NAME;
	}

	onRetailAdminDemoChange(enabled: boolean): void {
		if (!this.canToggleRetailAdminDemo || enabled === this.voucherDemoMode.isEnabled()) {
			return;
		}
		if (enabled) {
			this.viewAsScopeService.clearScope();
			this.devImpersonationService.clearToken();
		}
		this.voucherDemoMode.setEnabled(enabled);
		window.location.reload();
	}

	private loadViewAsState(): void {
		const scope = this.viewAsScopeService.getScope();
		if (scope && scope.enabled) {
			this.viewAsEnabled = true;
			this.viewAsRole = scope.role;
			this.viewAsDistiId = scope.distiId != null ? scope.distiId : null;
			this.viewAsClientId = scope.clientId != null ? scope.clientId : null;
			this.viewAsCampusId = scope.campusId != null ? scope.campusId : null;
			this.loadViewAsOptions();
		}
	}

	private loadViewAsOptions(): void {
		if (this.viewAsRole === 'distiadmin' && this.distributors.length === 0) {
			this.loadDistributors();
		}
		if (this.viewAsRole === 'clientadmin' && this.clients.length === 0) {
			this.loadClients();
		}
		if (this.viewAsRole === 'campusadmin' && this.campuses.length === 0) {
			this.loadCampuses();
		}
	}

	onViewAsEnabledChange(enabled: boolean): void {
		this.viewAsEnabled = enabled;
		if (!enabled) {
			this.viewAsScopeService.clearScope();
			this.viewAsRole = null;
			this.viewAsDistiId = null;
			this.viewAsClientId = null;
			this.viewAsCampusId = null;
			this.distributors = [];
			this.clients = [];
			this.campuses = [];
			window.location.reload();
		}
	}

	onViewAsRoleChange(role: ViewAsRole | null): void {
		this.viewAsRole = role;
		this.viewAsDistiId = null;
		this.viewAsClientId = null;
		this.viewAsCampusId = null;
		this.clients = [];
		this.campuses = [];
		if (role === 'distiadmin') {
			this.loadDistributors();
		} else if (role === 'clientadmin') {
			this.loadClients();
		} else if (role === 'campusadmin') {
			this.loadCampuses();
		}
		this.cdr.detectChanges();
	}

	private loadDistributors(): void {
		this.loadingDistributors = true;
		this.http.getDistis()
			.pipe(
				map((distis: Distributor[]) =>
					distis.map((d: Distributor) => ({
						value: String(d.disti_id),
						label: d.name,
						...d
					}))
				)
			)
			.subscribe(
				distis => {
					this.distributors = distis;
					this.loadingDistributors = false;
					this.cdr.detectChanges();
				},
				() => {
					this.loadingDistributors = false;
					this.cdr.detectChanges();
				}
			);
	}

	private loadClients(): void {
		this.loadingClients = true;
		const params: { disti_id?: string } = {};
		if (this.viewAsDistiId) {
			params.disti_id = this.viewAsDistiId;
		}
		this.http.getClients(params)
			.pipe(
				map((clients: Client[]) =>
					clients.map((c: Client) => ({
						value: String(c.client_id),
						label: c.company_name,
						...c
					}))
				)
			)
			.subscribe(
				clients => {
					this.clients = clients;
					this.loadingClients = false;
					this.cdr.detectChanges();
				},
				() => {
					this.loadingClients = false;
					this.cdr.detectChanges();
				}
			);
	}

	private loadCampuses(): void {
		this.loadingCampuses = true;
		const params: { client_id?: string; disti_id?: string } = {};
		if (this.viewAsClientId) {
			params.client_id = this.viewAsClientId;
		}
		if (this.viewAsDistiId) {
			params.disti_id = this.viewAsDistiId;
		}
		this.http.getCampus(params)
			.pipe(
				map((campuses: Campus[]) =>
					campuses.map((c: Campus) => ({
						value: String(c.campus_id),
						label: c.name,
						...c
					}))
				)
			)
			.subscribe(
				campuses => {
					this.campuses = campuses;
					this.loadingCampuses = false;
					this.cdr.detectChanges();
				},
				() => {
					this.loadingCampuses = false;
					this.cdr.detectChanges();
				}
			);
	}

	onViewAsDistiChange(distiId: string | null): void {
		this.viewAsDistiId = distiId;
		this.viewAsClientId = null;
		this.viewAsCampusId = null;
		this.campuses = [];
		if (this.viewAsRole === 'clientadmin' || (distiId && this.viewAsRole === 'campusadmin')) {
			this.loadClients();
		}
		if (distiId && this.viewAsRole === 'campusadmin') {
			this.loadCampuses();
		}
		this.cdr.detectChanges();
	}

	onViewAsClientChange(clientId: string | null): void {
		this.viewAsClientId = clientId;
		this.viewAsCampusId = null;
		if (clientId && this.viewAsRole === 'campusadmin') {
			this.loadCampuses();
		}
		this.cdr.detectChanges();
	}

	saveViewAs(): void {
		if (!this.viewAsScopeService.canEnable()) {
			return;
		}
		if (!this.viewAsEnabled || !this.viewAsRole) {
			this.viewAsScopeService.clearScope();
			window.location.reload();
			return;
		}
		const distiMatch = this.viewAsRole === 'distiadmin' && this.viewAsDistiId
			? this.distributors.find(d => d.value === this.viewAsDistiId) : null;
		const clientMatch = this.viewAsRole === 'clientadmin' && this.viewAsClientId
			? this.clients.find(c => c.value === this.viewAsClientId) : null;
		const campusMatch = this.viewAsRole === 'campusadmin' && this.viewAsCampusId
			? this.campuses.find(c => c.value === this.viewAsCampusId) : null;

		const scope: ViewAsScope = {
			enabled: true,
			role: this.viewAsRole,
			distiId: this.viewAsRole === 'distiadmin' ? this.viewAsDistiId : null,
			clientId: this.viewAsRole === 'clientadmin' ? this.viewAsClientId : null,
			campusId: this.viewAsRole === 'campusadmin' ? this.viewAsCampusId : null,
			distiName: distiMatch ? distiMatch.label : null,
			clientName: clientMatch ? clientMatch.label : null,
			campusName: campusMatch ? campusMatch.label : null
		};
		this.savingViewAs = true;
		this.viewAsScopeService.setScope(scope);
		window.location.reload();
	}

	getViewAsDisplayText(): string {
		const scope = this.viewAsScopeService.getScope();
		if (!scope || !scope.enabled || !scope.role) {
			return '';
		}
		const roleObj = this.viewAsRoles.find(r => r.value === scope.role);
		const roleLabel = roleObj ? roleObj.label : scope.role;
		const parts: string[] = [roleLabel];
		if (scope.distiId) {
			const d = this.distributors.find(x => x.value === scope.distiId);
			parts.push(d ? d.label : `Distributor ${scope.distiId}`);
		}
		if (scope.clientId) {
			const c = this.clients.find(x => x.value === scope.clientId);
			parts.push(c ? c.label : `Client ${scope.clientId}`);
		}
		if (scope.campusId) {
			const c = this.campuses.find(x => x.value === scope.campusId);
			parts.push(c ? c.label : `Campus ${scope.campusId}`);
		}
		return parts.join(' – ');
	}

  initForm() {
	this.profileForm = this.fb.group({
		firstName: [this.userObj.given_name || '', Validators.compose([
			Validators.required,
			Validators.minLength(3),
			Validators.maxLength(100)
		])
		],
		lastName: [this.userObj.family_name || '', Validators.compose([
			Validators.required,
			Validators.minLength(3),
			Validators.maxLength(100)
		])
		],
		email: [this.userObj.email || '', Validators.compose([
			Validators.required,
			Validators.email,
			Validators.minLength(3),
			Validators.maxLength(320)
		]),
		],
		// agree: [false, Validators.compose([Validators.required])]
	});
  }

  parseRole() {
	if (!this.userObj) {
		return '';
	}
	return formatRoleLabel(this.userObj['custom:role'], this.userObj[PORTAL_VIEW_CLAIM]);
  }

  parseRegionAndTerritory() {
	  if (this.userObj['custom:region_id'] || this.userObj['custom:territory_id']) {
		  this.http.getRegTerr(this.userObj['custom:region_id'], this.userObj['custom:territory_id'])
		  .subscribe(data => {
			console.log(data.region.region);
			if (data) {
				this.region = data.region.region;
				this.territory = data.territory.territory;
				this.cdr.detectChanges();
			}
		  });
	  }
  }

  updateAttributes() {
	Auth.currentAuthenticatedUser()
	.then(user => {
		Auth.updateUserAttributes(user, {
			email: this.profileForm.get('email').value,
			family_name: this.profileForm.get('lastName').value,
			given_name: this.profileForm.get('firstName').value
		})
		.then(value => {
			Auth.currentUserInfo()
			.then((userAttr: any) => {
				Cache.setItem('userAttr', userAttr.attributes);
				this.userObj = userAttr.attributes;
				this.showSnackBar('Updated Successfully', 0);
			});
		});
	});

  }

	/**
	 * Checking control validation
	 *
	 * @param controlName: string => Equals to formControlName
	 * @param validationType: string => Equals to valitors name
	 */
	isControlHasError(controlName: string, validationType: string): boolean {
		const control = this.profileForm.controls[controlName];
		if (!control) {
			return false;
		}

		const result = control.hasError(validationType) && (control.dirty || control.touched);
		return result;
	}

	showSnackBar(message: string, duration: number) {
		let snackRef = this._snackBar.open(message, 'close');
		if (duration > 0) {
			setTimeout(() => {
				this._snackBar.dismiss();
			}, duration);
		}

		snackRef.onAction().subscribe(() => {
			this._snackBar.dismiss();
		});
	}

}
